import { CHART_MARGIN, EXPANDED_CHART_MARGIN } from "./constants";
import { normalizeCanton } from "./canton-utils";

/* Generic helpers for charts */

// Compute chart dimensions; optional margins override
export const getChartDims = (
  isExpandedView: boolean,
  forcedHeight?: number,
  margins?: { top: number; right: number; left: number; bottom: number }
) => ({
  width: 800,
  height: forcedHeight ?? (isExpandedView ? 600 : 380),
  margin: margins ?? (isExpandedView ? EXPANDED_CHART_MARGIN : CHART_MARGIN),
});

// Re-export normalizeCanton from canton-utils for backward compatibility
export { normalizeCanton };

export const generateChartData = (
  activeTab: "companies" | "deals",
  filteredCompanies: any[],
  filteredDeals: any[]
) => {
  const currentData = activeTab === "companies" ? filteredCompanies : filteredDeals;

  if (activeTab === "companies") {
    const byYear: Record<number, number> = {},
      byIndustry: Record<string, number> = {},
      byCanton: Record<string, number> = {},
      byFunded: Record<string, number> = {};

    currentData.forEach((item) => {
      if (item.Year) byYear[item.Year] = (byYear[item.Year] || 0) + 1;

      if (item.Industry) {
        byIndustry[item.Industry] = (byIndustry[item.Industry] || 0) + 1;
      }

      if (item.Canton) byCanton[item.Canton] = (byCanton[item.Canton] || 0) + 1;

      const fundedStatus = item.Funded ? "Funded" : "Not Funded";
      byFunded[fundedStatus] = (byFunded[fundedStatus] || 0) + 1;
    });

    const realIndustries = Object.entries(byIndustry)
      .filter(([name]) => name)
      .sort((a, b) => (b[1] as number) - (a[1] as number))
      .map(([name]) => name);

    const allYears = Array.from(new Set(currentData.map((d: any) => d.Year).filter(Boolean))).sort(
      (a: number, b: number) => a - b
    );

    const industryTrends = realIndustries.map((industry) => {
      const industryData = allYears.map((year) => {
        const count = currentData.filter(
          (d: any) => d.Industry === industry && d.Year === year
        ).length;
        return { year, value: count, count, volume: 0 };
      });

      return { name: industry, data: industryData };
    });

    return {
      timeline: Object.entries(byYear)
        .map(([year, count]) => ({
          year: parseInt(year),
          count: count as number,
          volume: 0,
          label: year,
        }))
        .sort((a, b) => a.year - b.year),
      industries: Object.entries(byIndustry)
        .filter(([name]) => name)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => (b.value as number) - (a.value as number)),
      cantons: Object.entries(byCanton)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => (b.value as number) - (a.value as number))
        .slice(0, 15),
      funded: Object.entries(byFunded).map(([name, value]) => ({
        name,
        value,
      })),
      industryTrends,
    };
  }

  // DEALS
  const byYear: Record<number, number> = {},
    byYearVolume: Record<number, number> = {},
    byType: Record<string, number> = {},
    byPhase: Record<string, number> = {},
    byAmount: Record<string, number> = {},
    byCanton: Record<string, number> = {},
    byIndustryDeals: Record<string, Record<number, { count: number; volume: number }>> = {};

  currentData.forEach((item: any) => {
    if (item.Year) {
      byYear[item.Year] = (byYear[item.Year] || 0) + 1;
      byYearVolume[item.Year] = (byYearVolume[item.Year] || 0) + (item.Amount || 0);
    }
    if (item.Type) byType[item.Type] = (byType[item.Type] || 0) + 1;
    if (item.Phase) byPhase[item.Phase] = (byPhase[item.Phase] || 0) + 1;
    if (item.AmountRange) byAmount[item.AmountRange] = (byAmount[item.AmountRange] || 0) + 1;
    if (item.Canton) byCanton[item.Canton] = (byCanton[item.Canton] || 0) + 1;

    const industry = item.Industry;
    if (industry && item.Year) {
      if (!byIndustryDeals[industry]) byIndustryDeals[industry] = {};
      if (!byIndustryDeals[industry][item.Year]) {
        byIndustryDeals[industry][item.Year] = { count: 0, volume: 0 };
      }
      byIndustryDeals[industry][item.Year].count += 1;
      byIndustryDeals[industry][item.Year].volume += item.Amount || 0;
    }
  });

  const dealIndustryTrends = Object.entries(byIndustryDeals)
    .filter(([name]) => name)
    .map(([industry, yearData]) => ({
      name: industry,
      data: Object.entries(yearData)
        .map(([year, d]) => ({
          year: parseInt(year),
          count: d.count,
          volume: d.volume,
        }))
        .filter((d) => d.year)
        .sort((a, b) => a.year - b.year),
    }))
    .filter((it) => it.data.length > 0)
    .sort((a, b) => {
      const totalA = a.data.reduce((sum, d) => sum + (d.count as number), 0);
      const totalB = b.data.reduce((sum, d) => sum + (d.count as number), 0);
      return totalB - totalA;
    })
    .slice(0, 15);

  return {
    timeline: Object.entries(byYear)
      .map(([year, count]) => ({
        year: parseInt(year),
        count: count as number,
        volume: byYearVolume[Number(year)] || 0,
        label: year,
      }))
      .sort((a, b) => a.year - b.year),
    types: Object.entries(byType)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => (b.value as number) - (a.value as number)),
    phases: Object.entries(byPhase)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => (b.value as number) - (a.value as number)),
    amounts: Object.entries(byAmount).map(([name, value]) => ({ name, value })),
    cantons: Object.entries(byCanton)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => (b.value as number) - (a.value as number))
      .slice(0, 15),
    scatter: currentData
      .filter((d: any) => d.Amount && d.Valuation)
      .map((d: any) => ({
        x: d.Amount,
        y: d.Valuation,
        company: d.Company,
        canton: d.Canton,
        year: d.Year,
      })),
    industryTrends: dealIndustryTrends,
  };
};
